import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getTasks, getVapidKey, subscribePush, unsubscribePush, subscribeApns } from '../../utils/api';
import { parseLocalDate, isOverdue } from '../../utils/dates';

const isNative = () => !!(window.Capacitor?.isNativePlatform?.());

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
}

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [overdue, setOverdue] = useState([]);
  const [dueSoon, setDueSoon] = useState([]);
  const [pushState, setPushState] = useState('unknown'); // unknown | unsupported | denied | subscribed | unsubscribed
  const [pushWorking, setPushWorking] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    getTasks().then(({ data }) => {
      const now = new Date();
      const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      setOverdue(data.filter((t) => isOverdue(parseLocalDate(t.nextDue))));
      setDueSoon(data.filter((t) => {
        const d = parseLocalDate(t.nextDue);
        return !isOverdue(d) && d <= in7;
      }));
    });
  }, []);

  useEffect(() => {
    if (isNative()) {
      // On native iOS, check Capacitor push permission state
      import('@capacitor/push-notifications').then(({ PushNotifications }) => {
        PushNotifications.checkPermissions().then(({ receive }) => {
          setPushState(receive === 'granted' ? 'subscribed' : 'unsubscribed');
        }).catch(() => setPushState('unsubscribed'));
      });
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setPushState('denied');
      return;
    }
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription()
    ).then((sub) => {
      setPushState(sub ? 'subscribed' : 'unsubscribed');
    }).catch(() => setPushState('unsubscribed'));
  }, []);

  const handleEnablePush = async () => {
    setPushWorking(true);
    try {
      if (isNative()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { receive } = await PushNotifications.requestPermissions();
        if (receive !== 'granted') { setPushState('denied'); return; }
        await PushNotifications.register();
        // Token arrives via the 'registration' listener set up in index.js
        setPushState('subscribed');
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushState('denied'); return; }
      const { data } = await getVapidKey();
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await subscribePush(sub.toJSON());
      setPushState('subscribed');
    } catch (err) {
      console.error('Push subscribe failed', err);
    } finally {
      setPushWorking(false);
    }
  };

  const handleDisablePush = async () => {
    setPushWorking(true);
    try {
      if (isNative()) {
        // APNs tokens can't be unregistered client-side; just remove from server
        setPushState('unsubscribed');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint);
        await sub.unsubscribe();
      }
      setPushState('unsubscribed');
    } finally {
      setPushWorking(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const count = overdue.length + dueSoon.length;

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="notif-btn" onClick={() => setOpen((v) => !v)} aria-label="Notifications">
        🔔
        {count > 0 && <span className="notif-badge">{count}</span>}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-header">Notifications</div>
          {pushState === 'unsubscribed' && (
            <button
              className="notif-push-btn"
              onClick={handleEnablePush}
              disabled={pushWorking}
              style={{ width: '100%', margin: '0 0 0.5rem', padding: '0.4rem', fontSize: '0.8rem', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              {pushWorking ? 'Enabling…' : '🔔 Enable push notifications'}
            </button>
          )}
          {pushState === 'subscribed' && (
            <button
              className="notif-push-btn"
              onClick={handleDisablePush}
              disabled={pushWorking}
              style={{ width: '100%', margin: '0 0 0.5rem', padding: '0.4rem', fontSize: '0.8rem', background: 'transparent', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
            >
              {pushWorking ? '…' : 'Push on · Disable'}
            </button>
          )}
          {count === 0 ? (
            <p className="notif-empty">All caught up!</p>
          ) : (
            <>
              {overdue.length > 0 && (
                <div className="notif-group">
                  <div className="notif-group-label overdue-label">Overdue</div>
                  {overdue.map((t) => (
                    <Link key={t._id} to="/tasks" className="notif-item notif-overdue" onClick={() => setOpen(false)}>
                      {t.title}
                    </Link>
                  ))}
                </div>
              )}
              {dueSoon.length > 0 && (
                <div className="notif-group">
                  <div className="notif-group-label">Due within 7 days</div>
                  {dueSoon.map((t) => (
                    <Link key={t._id} to="/tasks" className="notif-item" onClick={() => setOpen(false)}>
                      {t.title}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
